import type * as React from "react"

import { cn } from "@/utils/styles.helpers"

export interface PageHeaderProps
	extends Omit<React.ComponentProps<"div">, "title"> {
	/** The page title, 20px semibold. */
	title: React.ReactNode
	/** An optional count shown after the title, such as the number of rows. */
	count?: React.ReactNode
	/** One line under the title saying what the page is for. Never UI instructions. */
	description?: React.ReactNode
	/** The page's actions, on the right. At most one primary button. */
	actions?: React.ReactNode
}

/** The top of a page's content: title, optional count and description, actions on the right. */
export function PageHeader({
	title,
	count,
	description,
	actions,
	className,
	...props
}: PageHeaderProps) {
	return (
		<div
			data-slot="page-header"
			className={cn(
				"flex flex-wrap items-start justify-between gap-x-4 gap-y-2",
				className,
			)}
			{...props}
		>
			<div className="flex min-w-0 flex-col gap-1">
				<h1 className="flex min-w-0 items-baseline gap-2.5 text-2xl font-semibold tracking-[-0.01em]">
					<span className="min-w-0 truncate">{title}</span>
					{count !== undefined && count !== null && (
						<span className="text-sm font-medium text-meta tabular-nums">
							{count}
						</span>
					)}
				</h1>
				{description && <p className="text-muted-foreground">{description}</p>}
			</div>
			{actions && (
				<div className="flex shrink-0 items-center gap-2">{actions}</div>
			)}
		</div>
	)
}
